import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner, Alert, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { apiGet } from '../../services/AuthService.js';

// Readable view of a regression diff log (GET a_regression/DiffLogView): every differing line with the
// changed characters highlighted, the changed fields named, and a summary of which fields differ most.
//
// The log grammar (VerifyService.DiffLogWriter): "Files were not a perfect match...", a header "OUT: <output
// blob>" / "REF: <control blob>", optional note lines, then one block per differing line:
//   Line #<n>
//   OUT: <output line>
//   REF: <control line>
// Values are everything after the 5-character prefix (never trimmed - CDEX records end in spaces). A quoted XLS
// cell can contain a newline, so a line with no prefix continues the previous value.

const PAGE_SIZE = 100;

// CDEX fixed-width layout (ExportService.GetCDEXString), 0-based offsets for a 2-character operator code. Fields
// marked shift move by (operator code length - 2) on record types 1 and 2; the type 3 trailer never moves.
const CDEX_FIELDS = {
    '1': [
        { key: 'recordType', label: 'Record type', start: 0, len: 1 },
        { key: 'operatorCode', label: 'Operator code', start: 1, len: 2 },
        { key: 'checkNumber', label: 'Check number', start: 3, len: 12, shift: true },
        { key: 'propertyCode', label: 'Property code (Operator CC)', start: 15, len: 14, shift: true },
        { key: 'fromPeriod', label: 'From production period', start: 29, len: 4, shift: true },
        { key: 'toPeriod', label: 'To production period', start: 33, len: 4, shift: true },
        { key: 'productCode', label: 'Product code', start: 37, len: 2, shift: true },
        { key: 'transactionCode1', label: 'Transaction code 1', start: 39, len: 2, shift: true },
        { key: 'leaseGrossVolume', label: 'Lease gross volume', start: 41, len: 12, shift: true },
        { key: 'leaseGrossSevTax', label: 'Lease gross severance tax', start: 53, len: 9, shift: true },
        { key: 'leaseNetValue', label: 'Lease net value', start: 62, len: 12, shift: true },
        { key: 'ownerLeaseNetValue', label: 'Owner lease net value', start: 74, len: 12, shift: true },
        { key: 'transactionCode2', label: 'Transaction code (tax/deduct)', start: 86, len: 2, shift: true },
        { key: 'propertyValue', label: 'Detail gross value', start: 88, len: 12, shift: true },
        { key: 'ownerValue', label: 'Detail owner value', start: 100, len: 12, shift: true },
        { key: 'transmissionDate', label: 'Julian transmission date', start: 112, len: 3, shift: true, volatile: true },
        { key: 'interestType', label: 'Interest type', start: 115, len: 1, shift: true },
        { key: 'decimalFlag', label: 'Decimal flag', start: 116, len: 1, shift: true },
        { key: 'decimalInterest', label: 'Decimal interest', start: 117, len: 9, shift: true },
        { key: 'btu', label: 'BTU factor', start: 126, endFromEol: 2, shift: true },
        { key: 'na', label: 'N/A (blank)', fromEnd: 2 },
    ],
    '2': [
        { key: 'recordType', label: 'Record type', start: 0, len: 1 },
        { key: 'operatorCode', label: 'Operator code', start: 1, len: 2 },
        { key: 'checkNumber', label: 'Check number', start: 3, len: 12, shift: true },
        { key: 'recordCount', label: 'Detail record count', start: 15, len: 4, shift: true },
        { key: 'checkAmount', label: 'Check amount', start: 19, len: 12, shift: true },
        { key: 'netOutFlag', label: 'Net-out flag', start: 31, len: 1, shift: true },
        { key: 'paymentFlag', label: 'Payment flag', start: 32, len: 1, shift: true },
        { key: 'checkDate', label: 'Check date (MMDD)', start: 33, len: 4, shift: true },
        { key: 'leaseGrossVolume', label: 'Total lease gross volume', start: 37, len: 13, shift: true },
        { key: 'leaseGrossSevTax', label: 'Total lease gross severance tax', start: 50, len: 10, shift: true },
        { key: 'leaseNetValue', label: 'Total lease net value', start: 60, len: 13, shift: true },
        { key: 'ownerLeaseNetValue', label: 'Total owner lease net value', start: 73, len: 13, shift: true },
        { key: 'propertyValue', label: 'Total gross taxes + deducts', start: 86, len: 13, shift: true },
        { key: 'ownerValue', label: 'Total owner taxes + deducts', start: 99, len: 13, shift: true },
        { key: 'transmissionDate', label: 'Julian transmission date', start: 112, len: 3, shift: true, volatile: true },
        { key: 'ownerNumber', label: 'Owner number', start: 115, len: 12, shift: true },
        { key: 'filler', label: 'Filler', start: 127, len: 5, shift: true },
    ],
    '3': [
        { key: 'recordType', label: 'Record type', start: 0, len: 1 },
        { key: 'checkRecordCount', label: 'Check count', start: 1, len: 5 },
        { key: 'checkTotal', label: 'File check total', start: 6, len: 14 },
        { key: 'filler', label: 'Filler', start: 20, len: 112 },
    ],
};

const PRODUCT_CODE_START = 37;
const isSign = (ch) => ch === '+' || ch === '-';

// Where each CDEX field sits on this line. opLen is the operator code length of the file this line came from
// (usually 2; the output and the control can differ).
function cdexFieldRanges(line, opLen) {
    const type = line.charAt(0);
    const fields = CDEX_FIELDS[type];
    if (!fields) return [];
    const shift = type === '3' ? 0 : opLen - 2;

    // A product with no CDEX mapping is written as the 1-character no-revenue product code (e.g. "P"), which moves
    // every later field of the detail record left by one. Lease gross volume always starts with its sign, right after
    // the "00" of transaction code 1, so the sign's position says which it is.
    let productShift = 0;
    if (type === '1') {
        const signAt = 41 + shift;
        if (!isSign(line.charAt(signAt)) && isSign(line.charAt(signAt - 1))) productShift = -1;
    }

    return fields.map(field => {
        if (field.key === 'operatorCode') return { ...field, start: 1, end: 1 + opLen };
        if (field.fromEnd != null) return { ...field, start: Math.max(0, line.length - field.fromEnd), end: line.length };
        const afterProduct = type === '1' && field.start > PRODUCT_CODE_START;
        const start = field.start + (field.shift ? shift : 0) + (afterProduct ? productShift : 0);
        const len = field.key === 'productCode' ? field.len + productShift : field.len;
        const end = field.endFromEol != null ? line.length - field.endFromEol : start + len;
        return { ...field, start, end: Math.max(start, end) };
    });
}

// Splits a CSV line the way the XLS controls are written (quotes only around cells with , " CR or LF).
function parseCsv(line) {
    const cells = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (quoted) {
            if (ch === '"') {
                if (line[i + 1] === '"') { cell += '"'; i++; } else { quoted = false; }
            } else {
                cell += ch;
            }
        } else if (ch === '"') {
            quoted = true;
        } else if (ch === ',') {
            cells.push(cell);
            cell = '';
        } else {
            cell += ch;
        }
    }
    cells.push(cell);
    return cells;
}

export function parseDiffLog(text) {
    const result = { outPath: null, refPath: null, notes: [], entries: [] };
    let current = null;
    let lastField = null;
    for (const line of (text || '').split(/\r?\n/)) {
        const lineMatch = /^Line #(\d+)$/.exec(line);
        if (lineMatch) {
            current = { lineNumber: Number(lineMatch[1]), out: null, ref: null };
            result.entries.push(current);
            lastField = null;
            continue;
        }
        if (line.startsWith('OUT: ')) {
            if (current && current.out === null) { current.out = line.slice(5); lastField = 'out'; } else if (!current && result.outPath === null) result.outPath = line.slice(5);
            continue;
        }
        if (line.startsWith('REF: ')) {
            if (current && current.ref === null) { current.ref = line.slice(5); lastField = 'ref'; } else if (!current && result.refPath === null) result.refPath = line.slice(5);
            continue;
        }
        if (line === '') { lastField = null; continue; }
        if (current && lastField) { current[lastField] += '\n' + line; continue; }
        if (line.startsWith('Files were not a perfect match')) continue;
        result.notes.push(line);
    }
    result.entries.forEach(entry => { entry.out = entry.out ?? ''; entry.ref = entry.ref ?? ''; });
    return result;
}

// Operator code length of each file, from a type 2 record (130 characters plus the code; the code can be blank).
// Worked out separately for the output and the control, which can differ. A side with no type 2 among the differing
// lines had a type 2 identical to the other side's, so it takes the other side's length.
export function detectOperatorCodeLengths(entries) {
    const fromSide = (side) => {
        const record = entries.find(entry => entry[side].charAt(0) === '2' && entry[side].length >= 130);
        return record ? record[side].length - 130 : null;
    };
    const out = fromSide('out');
    const ref = fromSide('ref');
    return { out: out ?? ref ?? 2, ref: ref ?? out ?? 2 };
}

// One file has a line where the other has none. The comparators write "<EOF>" for the missing side; the XLS
// comparator writes an empty control line when the output has extra rows.
function missingSide(entry, kind) {
    if (entry.out === '<EOF>' || (kind === 'xls' && entry.out === '' && entry.ref !== '')) return 'out';
    if (entry.ref === '<EOF>' || (kind === 'xls' && entry.ref === '' && entry.out !== '')) return 'ref';
    return null;
}

// The changed fields of one differing line: [{ label, out, ref, volatile }].
function describeEntry(entry, kind, context) {
    const missing = missingSide(entry, kind);
    if (missing) {
        return [{
            label: missing === 'out' ? 'Line missing from output' : 'Extra line in output',
            out: missing === 'out' ? '(none)' : 'line',
            ref: missing === 'ref' ? '(none)' : 'line',
            volatile: false,
        }];
    }
    if (kind === 'cdex') {
        // Lines out of step (an extra or missing record earlier in the file) put different record types side by side,
        // whose fields cannot be compared one for one.
        if (entry.out.charAt(0) !== entry.ref.charAt(0)) {
            return [{ label: 'Record type (lines out of step)', out: entry.out.charAt(0) || '(blank)', ref: entry.ref.charAt(0) || '(blank)', volatile: false }];
        }
        const outRanges = cdexFieldRanges(entry.out, context.opLen.out);
        const refRanges = cdexFieldRanges(entry.ref, context.opLen.ref);
        const ranges = outRanges.length ? outRanges : refRanges;
        return ranges
            .map((field, index) => {
                const refField = refRanges[index] || field;
                const out = entry.out.slice(field.start, field.end);
                const ref = entry.ref.slice(refField.start, refField.end);
                return out === ref ? null : { label: field.label, out: out.trim() === '' ? `"${out}"` : out, ref: ref.trim() === '' ? `"${ref}"` : ref, volatile: !!field.volatile };
            })
            .filter(Boolean);
    }
    if (kind === 'xls') {
        const outCells = parseCsv(entry.out);
        const refCells = parseCsv(entry.ref);
        const columns = Math.max(outCells.length, refCells.length);
        const changes = [];
        for (let i = 0; i < columns; i++) {
            const out = outCells[i] ?? '';
            const ref = refCells[i] ?? '';
            if (out !== ref) changes.push({ label: context.header[i] || `Column ${i + 1}`, out: out === '' ? '(blank)' : out, ref: ref === '' ? '(blank)' : ref });
        }
        return changes;
    }
    return [];
}

// Splits a value into same / different runs against the other side, for highlighting.
function highlightSegments(value, other, ignored) {
    const segments = [];
    for (let i = 0; i < value.length; i++) {
        const differs = value[i] !== other[i];
        const type = !differs ? 'same' : (ignored && ignored(i)) ? 'ignored' : 'diff';
        const last = segments[segments.length - 1];
        if (last && last.type === type) last.text += value[i]; else segments.push({ type, text: value[i] });
    }
    return segments;
}

const SEGMENT_STYLES = {
    same: {},
    diff: { backgroundColor: '#fde68a', color: '#7c2d12', fontWeight: 700 },
    ignored: { backgroundColor: '#e5e7eb', color: '#6b7280' },
};

function HighlightedLine({ prefix, value, other, ignored }) {
    return (
        <div style={{ fontFamily: 'Consolas, Menlo, monospace', fontSize: '0.76rem', whiteSpace: 'pre', lineHeight: 1.45 }}>
            <span style={{ color: '#9ca3af' }}>{prefix} </span>
            {highlightSegments(value, other, ignored).map((segment, index) => (
                <span key={index} style={SEGMENT_STYLES[segment.type]}>{segment.text}</span>
            ))}
        </div>
    );
}

export default function DiffLogViewer({ item, getAccessTokenSilently, onDownload, onClose }) {
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [shown, setShown] = useState(PAGE_SIZE);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await apiGet(`a_regression/DiffLogView?testItemId=${item.id}`, getAccessTokenSilently);
                if (cancelled) return;
                if (!response || !response.ok) {
                    setState({ loading: false, error: response && response.status === 404
                        ? 'No diff log was found for this file.'
                        : 'The diff log could not be loaded.', data: null });
                    return;
                }
                const data = await response.json();
                if (!cancelled) setState({ loading: false, error: null, data });
            } catch (error) {
                console.log(error);
                if (!cancelled) setState({ loading: false, error: 'The diff log could not be loaded.', data: null });
            }
        })();
        return () => { cancelled = true; };
    }, [item.id, getAccessTokenSilently]);

    const view = useMemo(() => {
        if (!state.data) return null;
        const kind = state.data.kind;
        const parsed = parseDiffLog(state.data.text);
        const context = {
            opLen: detectOperatorCodeLengths(parsed.entries),
            header: state.data.xlsHeader ? parseCsv(state.data.xlsHeader) : [],
        };
        const entries = parsed.entries.map(entry => ({ ...entry, changes: describeEntry(entry, kind, context) }));

        // Which fields differ, how often, and the most common output -> control pairs.
        const summary = new Map();
        entries.forEach(entry => entry.changes.filter(change => !change.volatile).forEach(change => {
            const row = summary.get(change.label) || { label: change.label, lines: 0, pairs: new Map() };
            row.lines += 1;
            const pair = `${change.out} → ${change.ref}`;
            row.pairs.set(pair, (row.pairs.get(pair) || 0) + 1);
            summary.set(change.label, row);
        }));
        const summaryRows = [...summary.values()]
            .sort((a, b) => b.lines - a.lines)
            .map(row => ({ ...row, topPairs: [...row.pairs.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3) }));

        // CDEX: the Julian transmission date is ignored by the comparison, so grey it out rather than highlight it.
        const ignoredFor = (line, side) => {
            if (kind !== 'cdex') return null;
            const julian = cdexFieldRanges(line, context.opLen[side]).find(f => f.volatile);
            return julian ? (i) => i >= julian.start && i < julian.end : null;
        };
        const showRawXls = (entry) => entry.changes.length === 0 || missingSide(entry, kind) !== null;
        return { kind, parsed, entries, summaryRows, ignoredFor, showRawXls };
    }, [state.data]);

    // The download reports failure here: the page's own banner is hidden behind this dialog.
    const [downloadError, setDownloadError] = useState(null);
    const download = async () => {
        setDownloadError(null);
        const error = await onDownload();
        if (error) setDownloadError(error);
    };

    return (
        <Modal show size="xl" scrollable onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.05rem' }}>
                    Diff log — {item.fileName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {state.loading && <div style={{ textAlign: 'center', padding: '24px' }}><Spinner animation="border" size="sm" /> Loading…</div>}
                {state.error && <Alert variant="warning" style={{ marginBottom: 0 }}>{state.error}</Alert>}

                {view && (
                    <>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '10px' }}>
                            <div><strong>Output:</strong> <span style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{view.parsed.outPath}</span></div>
                            <div><strong>Control:</strong> <span style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{view.parsed.refPath}</span></div>
                            {view.parsed.notes.map((note, index) => <div key={index} style={{ marginTop: '2px' }}>{note}</div>)}
                        </div>

                        <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '6px' }}>
                            {view.entries.length} differing line{view.entries.length === 1 ? '' : 's'}
                        </div>

                        {view.summaryRows.length > 0 && (
                            <Table size="sm" bordered style={{ fontSize: '0.78rem', marginBottom: '14px' }}>
                                <thead>
                                    <tr>
                                        <th>{view.kind === 'xls' ? 'Column' : 'Field'}</th>
                                        <th style={{ width: '90px' }}>Lines</th>
                                        <th>Most common (output → control)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {view.summaryRows.map(row => (
                                        <tr key={row.label}>
                                            <td style={{ fontWeight: 600 }}>{row.label}</td>
                                            <td>{row.lines}</td>
                                            <td style={{ fontFamily: 'Consolas, Menlo, monospace' }}>
                                                {row.topPairs.map(([pair, count]) => `${pair} ×${count}`).join(' · ')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}

                        {view.entries.slice(0, shown).map((entry, index) => (
                            <div key={index} style={{ borderTop: '1px solid #e5e7eb', padding: '6px 0' }}>
                                <div style={{ fontSize: '0.76rem', color: '#374151', marginBottom: '3px' }}>
                                    <strong>Line {entry.lineNumber}</strong>
                                    {view.kind === 'xls' && entry.lineNumber > 1 && <span style={{ color: '#9ca3af' }}> (data row {entry.lineNumber - 1})</span>}
                                    {entry.changes.filter(change => !change.volatile).map((change, changeIndex) => (
                                        <span key={changeIndex} style={{ marginLeft: '8px', padding: '1px 6px', borderRadius: '999px', background: '#fef3c7', color: '#7c2d12' }}>
                                            {change.label}: <span style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{change.out} → {change.ref}</span>
                                        </span>
                                    ))}
                                </div>
                                {/* XLS lines are long CSV rows, so the named cells above are the readable view; the raw
                                    lines are shown only when no cell could be named, or a row is extra/missing (to say which). */}
                                {view.kind === 'xls' ? (
                                    view.showRawXls(entry)
                                        ? (
                                            <div style={{ overflowX: 'auto' }}>
                                                <HighlightedLine prefix="OUT" value={entry.out} other={entry.ref} />
                                                <HighlightedLine prefix="REF" value={entry.ref} other={entry.out} />
                                            </div>
                                        )
                                        : null
                                ) : (
                                    <div style={{ overflowX: 'auto' }}>
                                        <HighlightedLine prefix="OUT" value={entry.out} other={entry.ref} ignored={view.ignoredFor(entry.out, 'out')} />
                                        <HighlightedLine prefix="REF" value={entry.ref} other={entry.out} ignored={view.ignoredFor(entry.ref, 'ref')} />
                                    </div>
                                )}
                            </div>
                        ))}

                        {view.entries.length > shown && (
                            <div style={{ textAlign: 'center', paddingTop: '8px' }}>
                                <Button size="sm" variant="outline-secondary" onClick={() => setShown(count => count + PAGE_SIZE)}>
                                    Show {Math.min(PAGE_SIZE, view.entries.length - shown)} more of {view.entries.length - shown} remaining
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {downloadError && <span style={{ fontSize: '0.8rem', color: '#921E1E', marginRight: 'auto' }}>{downloadError}</span>}
                <Button size="sm" variant="outline-secondary" onClick={download}>
                    <FontAwesomeIcon icon={faDownload} style={{ marginRight: 6 }} />Download log
                </Button>
                <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}
